import React, { useState, useRef, useEffect } from 'react';
import { iconsPath } from '../utils/imagePath';
import { Publish } from '../pages/Dashboard/GridPage';
import { clearAllEvents } from '../utils/UtilsFunction';
import axios from 'axios';
import useClickOutside from './UseClickOutside';
import {
  getTransitionSteps,
  options,
  trackingOptions,
} from '../utils/constant';

const bodyOptions = [
  {
    value: 'foot',
    label: 'Foot Tracking',
    description: 'Detects and maps foot movements to interact with virtual content.',
    enabled: true,
  },
  {
    value: 'hand',
    label: 'Hand Tracking',
    description: 'Interact with virtual objects using gestures and hands.',
    enabled: false,
  },
  {
    value: 'torso',
    label: 'Torso Tracking',
    description: 'Track torso movements and gestures.',
    enabled: false,
  },
  {
    value: 'wrist',
    label: 'Wrist Tracking',
    description: 'Track wrist movements and gestures.',
    enabled: false,
  },
];

const ARVRExperience = ({
  objectt,

  setName,
  category,
  projectName,
  setPublished,
  setCategory,
  setRedirectionCode,
}) => {
  const normalizeCategory = (cat) => {
    if (!cat) return 'ar';
    const valid = ['ar', 'vr', 'mr', 'xr'];
    const lower = cat.toLowerCase();
    return valid.includes(lower) ? lower : 'ar';
  };

  const [selectedOption, setSelectedOption] = useState(
    normalizeCategory(category)
  );
  const [previousOption, setPreviousOption] = useState('ar');
  const [currentStep, setCurrentStep] = useState(0);
  const [isTransitionModalOpen, setIsTransitionModalOpen] = useState(false);
  const [isMainDropdownOpen, setIsMainDropdownOpen] = useState(false); // For main experience dropdown
  const [isTrackingDropdownOpen, setIsTrackingDropdownOpen] = useState(false); // For tracking mode modal dropdown
  const [currentRecommendationIndex, setCurrentRecommendationIndex] =
    useState(0);
  const [transitionType, setTransitionType] = useState('');
  const [isTrackingModeModalOpen, setIsTrackingModeModalOpen] = useState(false);
  const [selectedTrackingMode, setSelectedTrackingMode] = useState(null);
  const [selectedBodyOption, setSelectedBodyOption] = useState(null);
  const [isBodyDropdownOpen, setIsBodyDropdownOpen] = useState(false);
  const [pendingTransition, setPendingTransition] = useState(null); // Store pending AR transition
  const [isConverting, setIsConverting] = useState(false);

  const mainDropdownRef = useRef(null);
  const trackingDropdownRef = useRef(null);
  const bodyDropdownRef = useRef(null);

  useEffect(() => {
    const normalized = normalizeCategory(category);
    if (normalized !== selectedOption) {
      setSelectedOption(normalized);
    }
  }, [category]);

  useClickOutside(mainDropdownRef, () => {
    setIsMainDropdownOpen(false);
  });
  useClickOutside(trackingDropdownRef, () => {
    setIsTrackingDropdownOpen(false);
  });
  useClickOutside(bodyDropdownRef, () => {
    setIsBodyDropdownOpen(false);
  });

  useEffect(() => {
    if (isTransitionModalOpen && currentStep === 2) {
      const currentTransitionSteps = getTransitionSteps(transitionType);
      const interval = setInterval(() => {
        setCurrentRecommendationIndex(
          (prev) =>
            (prev + 1) % currentTransitionSteps[2].recommendations.length
        );
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isTransitionModalOpen, currentStep, transitionType]);

  // Enhanced function to determine transition type based on current and target options
  const getTransitionType = (from, to) => {
    return `${from}-to-${to}`;
  };

  // Check if transition modal should be shown
  const shouldShowTransitionModal = (from, to) => {
    // Show modal for any transition between different experience types
    const transitionPairs = [
      // From AR
      ['ar', 'vr'],
      ['ar', 'mr'],
      ['ar', 'xr'],
      // From VR
      ['vr', 'ar'],
      ['vr', 'mr'],
      ['vr', 'xr'],
      // From MR
      ['mr', 'ar'],
      ['mr', 'vr'],
      ['mr', 'xr'],
      // From XR
      ['xr', 'ar'],
      ['xr', 'vr'],
      ['xr', 'mr'],
    ];

    return transitionPairs.some(
      ([source, target]) => from === source && to === target
    );
  };

  const handleOptionSelect = (option) => {
    const currentOption = selectedOption;
    const targetOption = option.value;

    if (shouldShowTransitionModal(currentOption, targetOption)) {
      setCurrentStep(0);
      setTransitionType(getTransitionType(currentOption, targetOption));
      setIsTransitionModalOpen(true);
      setPreviousOption(selectedOption);
    } else {
      // Direct switch for same option or unsupported transitions
      setSelectedOption(option.value);
      setPreviousOption(selectedOption);
    }
    setIsMainDropdownOpen(false);
  };

  async function updateProjectType(projectId, newCategory) {
    const token = localStorage.getItem('token');
    try {
      await axios.patch(
        `${process.env.REACT_APP_BASE_URL}experience/update/experience-name/${projectId}`,
        { category: newCategory.toUpperCase() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
    } catch (error) {
      console.error(
        `Failed to update project type to ${newCategory.toUpperCase()}:`,
        error
      );
    }
  }

  const handleDotClick = (index) => {
    setCurrentRecommendationIndex(index);
  };

  // Enhanced function to get target category from transition type
  const getTargetCategory = (transitionType) => {
    if (!transitionType) return 'ar';

    const parts = transitionType.split('-to-');
    return parts.length === 2 ? parts[1] : 'ar';
  };

 const handleNextStep = async () => {
   if (currentStep < 2) {
     setCurrentStep(currentStep + 1);
   } else {
     const newOption = getTargetCategory(transitionType);
     if (objectt?.scene) clearAllEvents(objectt.scene);

     if (newOption === 'ar') {
       setPendingTransition({ transitionType, targetOption: newOption });
       setSelectedOption(newOption);
       setIsTransitionModalOpen(false);
       setIsTrackingModeModalOpen(true);
     } else {
       setIsConverting(true); // 👈 start spinner
       try {
         const newOption = getTargetCategory(transitionType);
         if (objectt && setName) {
           if (setCategory) setCategory(newOption);
           objectt.category = newOption;
           if (objectt.projectId) {
             await updateProjectType(objectt.projectId, newOption);
           }
           await Publish(
             () => {},
             true,
             setName,
             objectt,
             null,
             null,
             setCategory,
             setRedirectionCode
           );
         }
         setSelectedOption(newOption);
         setIsTransitionModalOpen(false);
       } finally {
         setIsConverting(false); // 👈 stop spinner
       }
     }
   }
 };
  const handleCloseModal = () => {
    setIsTransitionModalOpen(false);
    setCurrentStep(0);
    setCurrentRecommendationIndex(0);
    setTransitionType('');
  };

  // Helper function to update tracking mode for existing projects
  async function updateTrackingMode(
    projectId,
    trackingMode,
    projectName,
    isAR
  ) {
    const token = localStorage.getItem('token');
    const endpoint = `experience/update/experience-name/${projectId}`;
    const body = {
      project_name: projectName,
      ...(isAR ? { category: trackingMode?.toUpperCase() } : {}),
    };

    try {
      await axios.patch(`${process.env.REACT_APP_BASE_URL}${endpoint}`, body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      // handle error
      console.error('Tracking mode API error:', error);
    }
  }

  // Handle tracking mode submission
  const handleTrackingModeSubmit = async () => {
    const finalTrackingMode =
      selectedTrackingMode?.value === 'body'
        ? selectedBodyOption?.value
        : selectedTrackingMode?.value;

    if (!finalTrackingMode) return;

    setIsConverting(true); // 👈 start spinner
    try {
      if (pendingTransition) {
        if (objectt?.scene) clearAllEvents(objectt.scene);
        if (objectt && setName) {
          const targetOption = pendingTransition.targetOption;
          if (setCategory) setCategory(targetOption);
          objectt.category = finalTrackingMode;
          if (objectt.projectId) {
            await updateTrackingMode(
              objectt.projectId,
              finalTrackingMode,
              projectName,
              targetOption === 'ar'
            );
          }
          await Publish(
            () => {},
            true,
            setName,
            objectt,
            null,
            null,
            setCategory,
            setRedirectionCode
          );
        }
      }
      setIsTrackingModeModalOpen(false);
      setPendingTransition(null);
      setSelectedTrackingMode(null);
      setSelectedBodyOption(null);
      setIsTrackingDropdownOpen(false);
      setIsBodyDropdownOpen(false);
    } finally {
      setIsConverting(false); // 👈 stop spinner
    }
  };

  // Handle tracking mode modal close
  const handleTrackingModeClose = () => {
    // If user closes the modal without submitting, revert the option
    if (pendingTransition) {
      setSelectedOption(previousOption);
    }
    setIsTrackingModeModalOpen(false);
    setPendingTransition(null);
    setSelectedTrackingMode(null);
    setSelectedBodyOption(null);
    setIsTrackingDropdownOpen(false); // Always close tracking dropdown on close
    setIsBodyDropdownOpen(false);
  };

  // Ensure dropdown always closes when modal closes (extra safety)
  useEffect(() => {
    if (!isTrackingModeModalOpen) {
      setIsTrackingDropdownOpen(false);
      setIsBodyDropdownOpen(false);
    }
  }, [isTrackingModeModalOpen]);

  const selectedOptionData = options.find(
    (opt) => opt.value === selectedOption
  );
  const dropdownOptions = options.filter((opt) => opt.value !== selectedOption);
  const currentTransitionSteps = getTransitionSteps(transitionType);
  const currentModal = currentTransitionSteps[currentStep];

  return (
    <div className="relative hidden md:block">
      <div className="relative" ref={mainDropdownRef}>
        <button
          onClick={() => setIsMainDropdownOpen(!isMainDropdownOpen)}
          className="bg-[#131418] text-white h-[30px] xl:w-[86px] lg:w-[80px] md:w-[68px] rounded-[40px] flex items-center justify-center gap-2 hover:bg-[#1a1b1e] transition-colors border border-[#37383F]"
        >
          <img
            src={selectedOptionData?.icon}
            alt="icon"
            className="object-contain xl:w-4 xl:h-4 lg:w-4 lg:h-4 md:w-3 md:h-3 hidden md:block"
          />
          <span className="font-medium xl:text-sm lg:text-sm md:text-xs">
            {selectedOptionData?.label}
          </span>
          <img
            className={`h-2 w-2 ${isMainDropdownOpen ? 'rotate-180' : ''}`}
            src={iconsPath?.arVrDownArrow}
            alt="arrow"
          />
        </button>

        {isMainDropdownOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-[#31333A] rounded-[10px] shadow-lg z-50 max-h-[165px] overflow-y-auto">
            <ul className="py-2">
              {dropdownOptions.map((option) => (
                <li
                  key={option.value}
                  onClick={() => {
                    handleOptionSelect(option);
                    setIsMainDropdownOpen(false);
                  }}
                  className="flex items-center xl:gap-3 lg:gap-3 md:gap-2 xl:px-4 lg:px-4 md:px-2 py-2 text-white hover:bg-[rgba(65,65,65,0.5)] hover:border hover:border-[#414141] border border-transparent rounded-[5px] cursor-pointer transition-colors"
                >
                  <img
                    className="object-contain xl:w-4 xl:h-4 lg:w-4 lg:h-4 md:w-3 md:h-3 hidden md:block"
                    src={option.icon}
                    alt={option.label}
                  />
                  <span className="font-medium xl:text-sm lg:text-sm md:text-xs">
                    {option.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Transition Modal */}
      {isTransitionModalOpen && currentModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50"
          onClick={handleCloseModal}
        >
          <div
            className="bg-[#121316] rounded-xl w-[375px] h-[260px] p-6 flex flex-col justify-between"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
                disabled={currentStep === 0}
                className="disabled:opacity-0"
              >
                <img
                  src={iconsPath?.group9Image}
                  alt="back"
                  className="w-4 h-4"
                />
              </button>
              <div className="flex items-center gap-2 mx-auto">
                <img
                  src={currentModal.icon}
                  alt="step-icon"
                  className="w-5 h-5"
                />
                <h3 className="text-[#ffffff] font-bold text-[16px]">
                  {currentModal.title}
                </h3>
              </div>
              <div className="w-4 h-4" />
            </div>

            <div className="text-[#AAAAAA] text-[14px] text-center h-[100px] flex items-center justify-center">
              <div>
                {Array.isArray(currentModal.description) ? (
                  <div className="text-left grid grid-cols-2 gap-x-11 gap-y-4 text-[14px]">
                    {currentModal.description.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <img
                          src={item.icon}
                          alt="icon"
                          className="w-4 h-4 flex-shrink-0"
                        />
                        <span className="text-[12px] leading-tight whitespace-nowrap">
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="whitespace-pre-line text-[14px]">
                    {currentModal.description}
                  </p>
                )}

                {currentStep === 2 && currentModal.recommendations && (
                  <>
                    <div className="h-[40px] mt-4 flex items-center justify-center overflow-hidden relative">
                      <div
                        key={currentRecommendationIndex}
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-md text-sm animate-slide-in-right text-[#EE2B2A]`}
                        style={{ animation: 'slideInRight 0.5s ease-out' }}
                      >
                        <img
                          src={
                            currentModal.recommendations[
                              currentRecommendationIndex
                            ].icon
                          }
                          alt="icon"
                          className="w-4 h-4"
                        />
                        <span>
                          {
                            currentModal.recommendations[
                              currentRecommendationIndex
                            ].text
                          }
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-center gap-2 mt-2">
                      {currentModal.recommendations.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => handleDotClick(index)}
                          className={`w-1 h-1 rounded-full transition-colors duration-300 ${
                            index === currentRecommendationIndex
                              ? 'w-6 h-2 bg-[#EE2B2A] rounded-full'
                              : 'w-2 h-2 bg-[#ffffff] rounded-full'
                          }`}
                        />
                      ))}
                    </div>

                    <style jsx>{`
                      @keyframes slideInRight {
                        0% {
                          transform: translateX(100%);
                          opacity: 0;
                        }
                        100% {
                          transform: translateX(0);
                          opacity: 1;
                        }
                      }
                    `}</style>
                  </>
                )}
              </div>
            </div>

            <div className="w-full mt-4">
              <button
                onClick={handleNextStep}
                disabled={isConverting}
                className="w-full px-6 py-2.5 bg-[#EE2B2A] text-white rounded-[8px] text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isConverting ? (
                  <>
                    Converting...
                    <svg
                      className="animate-spin w-4 h-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      />
                    </svg>
                  </>
                ) : (
                  <>
                    {currentStep < 2 ? 'Continue' : 'Confirm & Switch'}
                    <img
                      src={iconsPath?.experienceArrow}
                      alt="arrow"
                      className="w-4 h-4 object-contain"
                    />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Mode Modal */}
      {isTrackingModeModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4 z-50"
          onClick={handleTrackingModeClose}
        >
          <div
            className="bg-[#121316] rounded-xl w-full max-w-[375px] h-fit min-h-[260px] p-6 flex flex-col gap-5 relative text-left"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Dropdown Wrapper */}
            <div className="w-full relative" ref={trackingDropdownRef}>
              <label className="text-white text-[10px] flex items-center gap-1 mb-2">
                Tracking Mode
                <img
                  src={iconsPath?.trackingMode}
                  alt="info"
                  className="w-2 h-2"
                />
              </label>

              {/* Trigger Box */}
              <div
                className="w-full border border-[#454545] text-white rounded-[8px] px-4 py-2 cursor-pointer flex justify-between items-center"
                onClick={() => setIsTrackingDropdownOpen((prev) => !prev)}
              >
                <span className="text-[13px]">
                  {selectedTrackingMode
                    ? selectedTrackingMode.label
                    : 'Select tracking mode'}
                </span>
                <img
                  src={iconsPath?.vectorImage}
                  alt="arrow"
                  className={`w-3 h-3 transition-transform ${
                    isTrackingDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </div>

              {/* Dropdown Options */}
              {isTrackingDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#121316] border border-[#454545] scrollbar rounded-[8px] shadow-lg z-60 max-h-[158px] overflow-y-auto">
                  {trackingOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        setSelectedTrackingMode(option);
                        setSelectedBodyOption(null);
                        setIsTrackingDropdownOpen(false);
                      }}
                      className="px-4 py-2 hover:bg-[#454545] cursor-pointer"
                    >
                      <p className="text-white text-[13px]">{option.label}</p>
                      <p className="text-[#CBC5C5] text-[9px]">
                        {option.description}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sub-dropdown for Body options */}
            {selectedTrackingMode?.value === 'body' && (
              <div className="w-full relative" ref={bodyDropdownRef}>
                <label className="text-white text-[10px] flex items-center gap-1 mb-2">
                  Body Tracking Option
                </label>

                {/* Trigger Box */}
                <div
                  className="w-full border border-[#454545] text-white rounded-[8px] px-4 py-2 cursor-pointer flex justify-between items-center"
                  onClick={() => setIsBodyDropdownOpen((prev) => !prev)}
                >
                  <span className="text-[13px]">
                    {selectedBodyOption
                      ? selectedBodyOption.label
                      : 'Select body tracking option'}
                  </span>
                  <img
                    src={iconsPath?.vectorImage}
                    alt="arrow"
                    className={`w-3 h-3 transition-transform ${
                      isBodyDropdownOpen ? 'rotate-180' : ''
                    }`}
                  />
                </div>

                {/* Dropdown Options */}
                {isBodyDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-[#121316] border border-[#454545] scrollbar rounded-[8px] shadow-lg z-60 max-h-[158px] overflow-y-auto">
                    {bodyOptions.map((option) => (
                      <div
                        key={option.value}
                        onClick={() => {
                          if (option.enabled) {
                            setSelectedBodyOption(option);
                            setIsBodyDropdownOpen(false);
                          }
                        }}
                        className={`px-4 py-2 flex justify-between items-center ${
                          option.enabled
                            ? 'hover:bg-[#454545] cursor-pointer'
                            : 'opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <div>
                          <p className="text-white text-[13px]">{option.label}</p>
                          <p className="text-[#CBC5C5] text-[9px]">
                            {option.description}
                          </p>
                        </div>
                        {!option.enabled && (
                          <span className="text-[8px] bg-[#36373B] text-[#EDEDED] px-2 py-0.5 rounded-full whitespace-nowrap">
                            Coming soon
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Submit Button */}
            <button
              disabled={
                (!selectedTrackingMode || isConverting) ||
                (selectedTrackingMode?.value === 'body' && !selectedBodyOption)
              }
              onClick={handleTrackingModeSubmit}
              className={`w-full px-6 py-2.5 rounded-[8px] text-sm font-medium flex items-center justify-center gap-2 ${
                selectedTrackingMode &&
                (!isConverting &&
                  (selectedTrackingMode?.value !== 'body' || selectedBodyOption))
                  ? 'bg-[#EE2B2A] text-white'
                  : 'bg-[#36373B] text-[#ffffff] cursor-not-allowed'
              }`}
            >
              {isConverting ? (
                <>
                  Converting...
                  <svg
                    className="animate-spin w-4 h-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                </>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ARVRExperience;
